import {getHeaders, getMPPEndpoint} from '../../../../helpers/endpoint';
import {
  BonusQuestion,
  BonusQuestionGameWeek,
  BonusQuestionListItem,
  BonusQuestionMatch,
  BonusQuestionPayload,
  BonusQuestionResolvePayload,
  BonusQuestionApiStatus,
  BonusQuestionUpdatePayload,
  ChampionshipCalendarGameWeek,
  ChampionshipClubsById,
} from './types';

const getJson = (url: string, getState, onSuccess, onError) =>
  fetch(url, {
    method: 'GET',
    headers: getHeaders(getState().authentication),
  })
    .then(response => response.json())
    .then(data => {
      if (data?.error) {
        if (onError) onError(data.error);
        return;
      }
      onSuccess(data);
    })
    .catch(error => {
      if (onError) onError(error);
    });

const toArray = (data, key: string) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  return [];
};

export const fetchBonusQuestionGameWeeks = (
  championshipId: number,
  onSuccess: (gameWeeks: BonusQuestionGameWeek[]) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('getBonusQuestionGameWeeks', {championshipId}),
      getState,
      data => onSuccess(toArray(data, 'gameWeeks')),
      onError,
    );
};

export const fetchBonusQuestionGameWeekMatches = (
  championshipId: number,
  gameWeekNumber: number,
  onSuccess: (matches: BonusQuestionMatch[]) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('getBonusQuestionGameWeekMatches', {
        championshipId,
        gameWeekNumber,
      }),
      getState,
      data => onSuccess(toArray(data, 'matches')),
      onError,
    );
};

export const fetchChampionshipClubs = (
  onSuccess: (clubsById: ChampionshipClubsById) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('getChampionshipClubs'),
      getState,
      data => onSuccess(data?.championshipClubs || data || {}),
      onError,
    );
};

const sendJson = (
  url: string,
  method: string,
  body: any,
  getState,
  onSuccess,
  onError,
) =>
  fetch(url, {
    method,
    headers: getHeaders(getState().authentication),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
    .then(response => response.json())
    .then(data => {
      if (data?.error) {
        if (onError) onError(data.error);
        return;
      }
      onSuccess(data);
    })
    .catch(error => {
      if (onError) onError(error);
    });

/** `POST /bonus-question` */
export const createBonusQuestion = (
  payload: BonusQuestionPayload,
  onSuccess: (data: any) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    sendJson(
      getMPPEndpoint('createBonusQuestion'),
      'POST',
      payload,
      getState,
      onSuccess,
      onError,
    );
};

export const fetchBonusQuestions = (
  championshipId: number,
  onSuccess: (questions: BonusQuestionListItem[]) => void,
  onError?: (error: unknown) => void,
  status?: BonusQuestionApiStatus,
  season?: number,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('getBonusQuestions', {championshipId, season, status}),
      getState,
      data => onSuccess(toArray(data, 'bonusQuestions')),
      onError,
    );
};

/** `GET /bonus-question/:id` */
export const fetchBonusQuestion = (
  bonusQuestionId: string,
  onSuccess: (bonusQuestion: BonusQuestion) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('bonusQuestion', bonusQuestionId),
      getState,
      data => onSuccess(data?.bonusQuestion || data),
      onError,
    );
};

/** `PATCH /bonus-question/:id` */
export const updateBonusQuestion = (
  bonusQuestionId: string,
  payload: BonusQuestionUpdatePayload,
  onSuccess: (data: any) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    sendJson(
      getMPPEndpoint('bonusQuestion', bonusQuestionId),
      'PATCH',
      payload,
      getState,
      onSuccess,
      onError,
    );
};

/** `PATCH /bonus-question/:id/cancel` — sert aussi bien à "Supprimer" qu'à "Annuler". */
export const cancelBonusQuestion = (
  bonusQuestionId: string,
  onSuccess: (data: any) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    sendJson(
      getMPPEndpoint('cancelBonusQuestion', bonusQuestionId),
      'PATCH',
      undefined,
      getState,
      onSuccess,
      onError,
    );
};

/** `PATCH /bonus-question/:id/resolve` — saisie de la bonne réponse. */
export const resolveBonusQuestion = (
  bonusQuestionId: string,
  payload: BonusQuestionResolvePayload,
  onSuccess: (data: any) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    sendJson(
      getMPPEndpoint('resolveBonusQuestion', bonusQuestionId),
      'PATCH',
      payload,
      getState,
      onSuccess,
      onError,
    );
};

/**
 * `GET /championship-calendar/:championshipId` : toutes les journées de la saison,
 * passées comprises. À la différence de `fetchBonusQuestionGameWeeks`, qui ne
 * renvoie que les journées à venir éligibles à une nouvelle question.
 */
export const fetchChampionshipCalendarGameWeeks = (
  championshipId: number,
  onSuccess: (gameWeeks: ChampionshipCalendarGameWeek[]) => void,
  onError?: (error: unknown) => void,
) => {
  return (_dispatch, getState) =>
    getJson(
      getMPPEndpoint('getChampionshipCalendarByChampionshipId', championshipId),
      getState,
      data => {
        const gameWeeks = data?.gameWeeks || {};
        onSuccess(
          Object.keys(gameWeeks)
            .map(gameWeekNumber => gameWeeks[gameWeekNumber])
            .filter(gameWeek => Boolean(gameWeek)),
        );
      },
      onError,
    );
};



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/api.ts